import React from 'react';
// template components
import Accordion from '@nmx/template/dist/componentLibrary/modules/Accordion';
import Col from '@nmx/template/dist/componentLibrary/foundations/Grid/Col';
import Link from '@nmx/template/dist/componentLibrary/components/Link';
import Picture from '@nmx/template/dist/componentLibrary/components/Picture';
import Row from '@nmx/template/dist/componentLibrary/foundations/Grid/Row';
import SectionContainer from '@nmx/template/dist/componentLibrary/components/SectionContainer';
import Typography from '@nmx/template/dist/componentLibrary/foundations/Typography';
import WidowBlock from '@nmx/template/dist/componentLibrary/components/WidowBlock';
// icons
import CaratRight from '@nmx/template/dist/componentLibrary/foundations/Icon/icons/utility/CaratRight';
import MinusSignFillIcon from '@nmx/template/dist/componentLibrary/foundations/Icon/icons/utility/MinusSignFill';
import PlusSignFillIcon from '@nmx/template/dist/componentLibrary/foundations/Icon/icons/utility/PlusSignFill';
// images
import washSalesGraphicDesktopSvg from './images/wash-sale-graphic-desktop.svg';
import washSalesGraphicMobileSvg from './images/wash-sale-graphic-mobile.svg';
// styles
import {
  ColStyled,
  DescriptionListStyled,
  RowStyled,
} from './styles';

const faqIdPrefix = 'faq-tax-documents-';
const faqIdSuffix = '-faq';

export default () => (
  <SectionContainer
    id='tax-documents-faq' // '-section-container' will be appended dynamically
    moduleName='faq-module'
    moduleVariation='A'>
    <Row align='center'>
      <Col
        align='center'
        medium={10}
        large={8}>
        <Typography
          component='h2'
          nextElement='graphic'>
          Questions about taxes and your money&#63; <WidowBlock>We&#39;ve got answers.</WidowBlock>
        </Typography>
      </Col>
    </Row>
    <RowStyled align='center'>
      {/* start first faq group */}
      <ColStyled
        medium={10}
        large={8}>
        <Typography
          component='h3'
          align='center'
          disableBottomPadding>
          Retirement accounts
        </Typography>
        <Accordion
          id={`${faqIdPrefix}-retirement-accounts`}
          variant='secondary'
          toggleOpenIcon={PlusSignFillIcon}
          toggleCloseIcon={MinusSignFillIcon}
          isFAQ
          items={[
            {
              id: `${faqIdPrefix}-reporting-ira-conversions-${faqIdSuffix}`,
              title: (<>How are Roth IRA <WidowBlock>conversions reported&#63;</WidowBlock></>),
              contentAs: 'div',
              content: (
                <>
                  <Typography nextElement='typography'>
                    A conversion is a reportable distribution from a Traditional IRA to a Roth IRA. It may or may not be a taxable event, and you should always consult your tax advisor before completing a conversion. The IRS requires financial firms to issue a 1099&#45;R for any distributions from retirement accounts, including conversions, as described in{' '}
                    <Link
                      id={`${faqIdPrefix}-reporting-ira-conversions-irs-link`}
                      linkUrl='https://www.irs.gov/pub/irs-pdf/p590a.pdf'
                      variant='primaryDeemphasize'
                      ariaLabel='Download the IRS Publication 590-A PDF'
                      isOutgoingLink>
                      Publication 590&#45;A
                    </Link>.
                  </Typography>
                  <Typography disableBottomPadding>
                    Find more detailed information about your Roth IRA conversion on your Roth IRA year&#45;end statement.
                  </Typography>
                </>
              ),
            },
            {
              id: `${faqIdPrefix}-qualified-charitable-distribution-${faqIdSuffix}`,
              title: (<>Does a qualified charitable distribution trigger <WidowBlock>a 1099&#45;R&#63;</WidowBlock></>),
              contentAs: 'div',
              content: (
                <Typography disableBottomPadding>
                  As your IRA custodian, we cannot decide whether a distribution satisfies the IRS requirements for the treatment of a qualified charitable distribution &#40;QCD&#41;. We&#39;re required to issue a 1099&#45;R for any distribution. Review{' '}
                  <Link
                    id={`${faqIdPrefix}-qualified-charitable-distribution-irs-newsroom-link`}
                    linkUrl='https://www.irs.gov/newsroom/qualified-charitable-distributions-allow-eligible-ira-owners-up-to-100000-in-tax-free-gifts-to-charity/'
                    variant='primaryDeemphasize'
                    isOutgoingLink
                    noWrap>
                    IR&#45;2023&#45;215, Nov. 16, 2023
                  </Link>
                  , published on the IRS website. Ask your tax professional about how to report your qualified charitable distributions.
                </Typography>
              ),
            },
            {
              id: `${faqIdPrefix}-multiple-1099s-${faqIdSuffix}`,
              title: (<>Should there be multiple 1099&#45;Rs for <WidowBlock>one account&#63;</WidowBlock></>),
              contentAs: 'div',
              content: (
                <Typography disableBottomPadding>
                  Based on your IRA account activity, multiple 1099&#45;Rs may be issued to reflect different withdrawal transaction types. Ask your tax professional any questions about multiple 1099&#45;Rs.
                </Typography>
              ),
            },
          ]}
        />
      </ColStyled>
      {/* start second faq group */}
      <ColStyled
        medium={10}
        large={8}>
        <Typography
          component='h3'
          align='center'
          disableBottomPadding>
          Investment accounts
        </Typography>
        <Accordion
          id={`${faqIdPrefix}-investment-accounts`}
          variant='secondary'
          toggleOpenIcon={PlusSignFillIcon}
          toggleCloseIcon={MinusSignFillIcon}
          isFAQ
          items={[
            {
              id: `${faqIdPrefix}-composite-1099-form-${faqIdSuffix}`,
              title: (<>What is a composite <WidowBlock>1099 form&#63;</WidowBlock></>),
              contentAs: 'div',
              content: (
                <Typography disableBottomPadding>
                  A composite 1099 gives you a summary of IRS 1099 forms &#40;1099&#45;B, 1099&#45;DIV, 1099&#45;INT, 1099&#45;MISC, 1099&#45;OID&#41; on one document for tax reporting. If you&#39;ve received more than &#36;10 in dividends or interest or any gross proceeds from the sale or maturity of a security, expect to receive a composite 1099.
                </Typography>
              ),
            },
            {
              id: `${faqIdPrefix}-pending-1099-notification-${faqIdSuffix}`,
              title: (<>What is a pending <WidowBlock>1099 notification&#63;</WidowBlock></>),
              contentAs: 'div',
              content: (
                <>
                  <Typography nextElement='typography'>
                    A pending notice is created when security issuers have not provided their final tax information to Northwestern Mutual or if the information is still pending review or processing. These notices are sent in February. They show the impacted securities and the expected delivery date of the tax document.
                  </Typography>
                  <Typography disableBottomPadding>
                    If you&#39;ve elected to receive e&#45;Delivery notifications, you‘ll receive an email alert for any pending 1099 notices. If not, any notifications will be mailed to you.
                  </Typography>
                </>

              ),
            },
            {
              id: `${faqIdPrefix}-revised-1099-composite-${faqIdSuffix}`,
              title: (<>Why are revised 1099 composite <WidowBlock>forms issued&#63;</WidowBlock></>),
              contentAs: 'div',
              content: (
                <Typography disableBottomPadding>
                  There are several reasons why a revised 1099 composite would be issued and sent to you, including late reporting or income reclassifications by security issuers, updated Original Issue Discount &#40;OID&#41; rates for bonds, and foreign tax withholding adjustments.
                </Typography>
              ),
            },
            {
              id: `${faqIdPrefix}-k1-tax-forms-${faqIdSuffix}`,
              title: (<>Who issues K&#45;1 <WidowBlock>tax forms&#63;</WidowBlock></>),
              contentAs: 'div',
              content: (
                <Typography disableBottomPadding>
                  A Schedule K&#45;1 is a tax form issued on an annual basis directly from the partnership. It is used to report each partner&#39;s share of the partnership&#39;s earnings, losses, deductions, and credits. K&#45;1s are issued to clients directly from a partnership. You can find their K&#45;1 forms on their company website.
                </Typography>
              ),
            },
            {
              id: `${faqIdPrefix}-multiple-investment-accounts-${faqIdSuffix}`,
              title: (<>What are the tax rules for multiple investment accounts&#63;</>),
              contentAs: 'div',
              content: (
                <Typography disableBottomPadding>
                  If you have more than one account, tax forms are issued for each account. Depending on the account type, its holdings and activity, you may receive tax forms at different times.
                </Typography>
              ),
            },
            {
              id: `${faqIdPrefix}-wash-sale-${faqIdSuffix}`,
              title: (<>What is a <WidowBlock>wash sale&#63;</WidowBlock></>),
              contentAs: 'div',
              content: (
                <>
                  <Typography nextElement='typography'>
                    A wash sale occurs when you sell an investment at a loss and buy or reinvest back into the same or a &#34;substantially identical&#34; investment within 30 days before or after the sale.
                  </Typography>
                  <Typography nextElement='typography'>
                    The consequence of a wash sale is that your loss is not allowed for the current tax year however it is added to the cost basis of the repurchased shares postponing the loss deduction until the newly repurchased shares are sold.
                  </Typography>
                  <Typography nextElement='typography'>
                    Northwestern Mutual must track, and report covered security wash sales that occur for the same security within the same account. However, note that wash sale rules apply to other accounts with the same owner, beneficially owned accounts &#40;e.g., spouse&#41; and tax&#45;qualified accounts &#40;e.g., IRA&#41;. Therefore, it is important that you track, and report wash sales across all your investment accounts, whether held at Northwestern Mutual Investment Services, Northwestern Mutual Wealth Management Company or other financial institutions.
                  </Typography>
                  <Typography nextElement='typography'>
                    For a more in&#45;depth perspective about the tax treatment of a wash sale, review{' '}
                    <Link
                      id={`${faqIdPrefix}-wash-sale-irs-publication-550-link`}
                      linkUrl='https://www.irs.gov/pub/irs-pdf/p550.pdf'
                      variant='primaryDeemphasize'
                      ariaLabel='Download the IRS Publication 550 PDF'
                      isOutgoingLink>
                      IRS Publication 550
                    </Link>.
                  </Typography>
                  <Typography
                    nextElement='graphic'
                    weight={500}
                    italic>
                    This information is not intended as tax advice. Consult your tax professional for advice that is specific to your situation.
                  </Typography>
                  <Picture
                    src={washSalesGraphicDesktopSvg}
                    mobileSrc={washSalesGraphicMobileSvg}
                    altText='Buy: 30 days before; Sell: Sale of security at a loss; Buy 30 days after. If the same security, or substantially similar security, is purchased during this period then the wash sales rules apply.'
                    lazyLoad
                    fullWidth
                  />
                </>
              ),
            },
          ]}
        />
      </ColStyled>
      {/* start third faq group */}
      <Col
        medium={10}
        large={8}>
        <Typography
          component='h3'
          align='center'
          disableBottomPadding>
          Insurance &#38; annuities
        </Typography>
        <Accordion
          id={`${faqIdPrefix}-insurance-and-annuities`}
          variant='secondary'
          toggleOpenIcon={PlusSignFillIcon}
          toggleCloseIcon={MinusSignFillIcon}
          isFAQ
          items={[
            {
              id: `${faqIdPrefix}-1099-form-${faqIdSuffix}`,
              title: (<>Why would Northwestern Mutual send a <WidowBlock>1099 form&#63;</WidowBlock></>),
              contentAs: 'div',
              content: (
                <>
                  <Typography nextElement='typography'>
                    The Internal Review Code &#40;IRC&#41; requires Northwestern Mutual to report certain distributions and interest earned from life insurance and annuity contracts.
                  </Typography>
                  <Typography nextElement='typography'>
                    The IRS requires that we report this information on a 1099 form to both the taxpayer and the IRS. Some common examples of taxable income Northwestern Mutual is required to report include:
                  </Typography>
                  <DescriptionListStyled
                    descriptionListData={[
                      {
                        descriptionTerm: <>Form 1099&#45;INT:</>,
                        descriptionDetails: <>If you earned at least &#36;10 in interest, we will issue a 1099&#45;INT on interest earned from a billing or payment account, dividends that are left to accumulate, death proceeds after the date of death &#40;prior to the proceeds being paid&#41;, access fund accounts, and interest income plans.</>,
                      },
                      {
                        descriptionTerm: <>Form 1099&#45;R:</>,
                        descriptionDetails: <>If you received a full and partial distribution or certain dividend payments from a life insurance or annuity, we would issue a 1099&#45;R. Also, some tax&#45;free exchanges are also reportable on a 1099&#45;R for informational purposes.</>,
                      },
                    ]}
                    disableBullet
                  />
                </>
              ),
            },
            {
              id: `${faqIdPrefix}-contract-dividend-${faqIdSuffix}`,
              title: (<>Should a contract dividend be reported on <WidowBlock>a 1099&#45;DIV&#63;</WidowBlock></>),
              contentAs: 'div',
              content: (
                <Typography disableBottomPadding>
                  No. Form 1099&#45;DIV is generally used for dividends paid on corporate stock. Taxable dividend distributions from life insurance and annuity contracts are reported on a 1099&#45;R form.
                </Typography>
              ),
            },
            {
              id: `${faqIdPrefix}-tax-penalty-early-distributions-${faqIdSuffix}`,
              title: (<>Is there a tax penalty for early distributions if <WidowBlock>you&#39;re disabled&#63;</WidowBlock></>),
              contentAs: 'div',
              content: (
                <>
                  <Typography nextElement='typography'>
                    While you may qualify for the disability exception, the definition of disability can vary. You must meet the IRS definition of disability to qualify. Because Northwestern Mutual cannot make the determination of whether you qualify under the IRS definition, you received a 1099&#45;R form with a distribution code of &#34;1&#34; in box 7 indicating that you were subject to a penalty tax because at the time of distribution you were younger than age 59 &#189;.
                  </Typography>
                  <Typography disableBottomPadding>
                    Consult your tax advisor regarding claiming the exception on your federal tax return.
                  </Typography>
                </>
              ),
            },
            {
              id: `${faqIdPrefix}-taxable-gain-1099r-${faqIdSuffix}`,
              title: (<>How should the taxable gain on 1099&#45;R form <WidowBlock>be reported&#63;</WidowBlock></>),
              contentAs: 'div',
              content: (
                <Typography disableBottomPadding>
                  Generally, the gain reported on the full or partial distribution of a life insurance or annuity contract is considered ordinary income.
                </Typography>
              ),
            },
            {
              id: `${faqIdPrefix}-irs-b-notice-${faqIdSuffix}`,
              title: (<>Why would the IRS send <WidowBlock>a B&#45;Notice&#63;</WidowBlock></>),
              contentAs: 'div',
              content: (
                <Typography disableBottomPadding>
                  The IRS will issue this letter when an SSN or TIN is not provided to us in the required manner or does not match IRS records for tax forms Northwestern Mutual filed the previous year. To learn more about this, read{' '}
                  <Link
                    id={`${faqIdPrefix}-irs-b-notice-link`}
                    linkUrl='https://www.irs.gov/pub/irs-pdf/p1281.pdf'
                    variant='primaryDeemphasize'
                    ariaLabel='Download the IRS Publication 1281 PDF'
                    isOutgoingLink
                    noWrap>
                    IRS Publication 1281
                  </Link>
                  , &#34;Backup Withholding for Missing and Incorrect Name&#47;TIN&#40;s&#41;&#34;.
                </Typography>
              ),
            },
            {
              id: `${faqIdPrefix}-modified-endowment-contract-${faqIdSuffix}`,
              title: (<>What is a modified endowment <WidowBlock>contract (MEC)&#63;</WidowBlock></>),
              contentAs: 'div',
              content: (
                <Typography disableBottomPadding>
                  A permanent life insurance policy is classified as a MEC if the cumulative premium paid exceeds the premium limit established by the IRC. A MEC policy is taxed less favorably than a non&#45;MEC policy. Consult your financial professional or tax advisor to learn more.
                </Typography>
              ),
            },
            {
              id: `${faqIdPrefix}-tax-withdrawals-from-insurance-policy-${faqIdSuffix}`,
              title: (<>Is there a tax on withdrawals from a life <WidowBlock>insurance policy&#63;</WidowBlock></>),
              contentAs: 'div',
              content: (
                <>
                  <Typography nextElement='typography'>
                    For any part of a transaction to be taxable, the contract must first have a gain. A life insurance contract has a gain when the cash value exceeds the cost basis &#40;generally premiums paid less adjustments&#41; of the contract.
                  </Typography>
                  <Typography disableBottomPadding>
                    Typically, when you take a distribution from a life insurance contract the IRC allows you to recover the cost basis before any gain is withdrawn. But there are some exceptions. Distributions &#40;including loans&#41; from life insurance contracts classified as MECs are taxed gain&#45;out first and may be subject to an additional 10&#37; penalty tax. Also, distributions from a contract within the first 15 contract years may be subject to gain&#45;out&#45;first taxation even if the policy is not a MEC, depending on the size of the withdrawal and other factors.
                  </Typography>
                </>
              ),
            },
            {
              id: `${faqIdPrefix}-multiple-1099s-${faqIdSuffix}`,
              title: (<>Why do I see multiple images of some of <WidowBlock>my 1099s&#63;</WidowBlock></>),
              contentAs: 'div',
              content: (
                <Typography disableBottomPadding>
                  If you have multiple life and&#47;or annuity accounts that generate a 1099, Northwestern Mutual may have consolidated it into one tax document if certain criteria are met. For more information about this, see details on page 3 of your 1099.
                </Typography>
              ),
            },
          ]}
        />
      </Col>
    </RowStyled>
    <Row>
      <Col>
        <Typography
          align='center'
          disableBottomPadding>
          Have more questions&#63;{' '}
          <Link
            id={`${faqIdPrefix}-see-more-faqs-link`}
            variant='primaryDeemphasize'
            linkUrl='/faq/annuity-faq/'
            endAdornment={CaratRight}
            endAdornmentStylesReset>
            See more FAQs
          </Link>
        </Typography>
      </Col>
    </Row>
  </SectionContainer>
);
